import React, { useEffect, useState, forwardRef } from "react";
import {
  getProductById,
  getProductVariationById,
  getProductOrderItemsByVariation,
  getImagesByVariationId,
  getOrCreateInventory,
  getProductVariationsByProductId,
} from "../../core/api/products";
import { getMediaPlansAndItemEstimationsByProductVariation } from "../../core/api/mediaplan";
import { Modal } from "react-bootstrap";
import InventorySection from "./inventorysection";

const ProductDetailsView = forwardRef(
  (
    {
      isVisible,
      productId,
      onResize,
      variationId,
      warehouseId,
    },
    ref
  ) => {
    // General states
    const [product, setProduct] = useState(null);
    const [productVariations, setProductVariations] = useState([]);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalImage, setModalImage] = useState("");
    const [actualStockLevel, setActualStockLevel] = useState(0);
    const [inventoryDate, setInventoryDate] = useState("");
    const [orders, setOrders] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Reset state when component is closed
    const resetState = () => {
      setProduct(null);
      setProductVariations([]);
      setActualStockLevel(0);
      setInventoryDate("");
      setOrders([]);
      setCampaigns([]);
    };

    // Fetch product details
    useEffect(() => {
      if (isVisible && productId) {
        const fetchProductDetails = async () => {
          try {
            const productResponse = await getProductById(productId);
            setProduct(productResponse.data);
            const variationsResponse = await getProductVariationsByProductId(
              productId
            );
            const variations = variationsResponse.data || [];
            const variationsWithImages = await Promise.all(
              variations.map(async (variation) => {
                const imagesResponse = await getImagesByVariationId(
                  variation.id
                );
                return { ...variation, images: imagesResponse.data };
              })
            );
            setProductVariations(variationsWithImages);
          } catch (error) {
            console.error("Error fetching product details:", error);
          }
        };

        fetchProductDetails();
      } else if (!isVisible) {
        resetState();
      }
    }, [isVisible, productId]);

     

    const [variation, setVariation] = useState(null);
    useEffect(() => {
      if (!variationId) return;
      const fetchStockAndCampaignDetails = async () => {
        const variationRes = await getProductVariationById(variationId);
        setVariation(variationRes.data);

        setIsLoading(true);

        try {
          const ordersResponse = await getProductOrderItemsByVariation(
            variationId
          );
          const fetchedOrders = ordersResponse.data.map((order) => ({
            order: order.product_order_detail?.invoice_number,
            date: order.product_order_detail?.invoice_date,
            totalQty: order.quantity,
            delivered: order.delivered,
            pending: order.pending,
          }));
          setOrders(fetchedOrders);
          if (warehouseId) {
            const inventoryResponse = await getOrCreateInventory(
              warehouseId,
              variationId
            );
            setActualStockLevel(inventoryResponse.data.inventory_on_hand);
            setInventoryDate(inventoryResponse.data.inventory_date);
          }

          const campaignsResponse =
            await getMediaPlansAndItemEstimationsByProductVariation(
              variationId
            );
          const fetchedCampaigns = campaignsResponse.data.map((campaign) => ({
            date: campaign.date,
            campaignCode: campaign.campaign_code,
            qty: campaign.target_mail_qty,
            rateOfResponse: campaign.target_rate_of_response * 0.01,
            estimatedPerOrder: campaign.qty_per_order,
          }));
          setCampaigns(fetchedCampaigns);
        } catch (error) {
          console.error("Error fetching stock and campaign details:", error);
        }

        setIsLoading(false);
      };

      fetchStockAndCampaignDetails();
    }, [variationId]);

    useEffect(() => {
      const sidebarWidth = isFullScreen ? window.innerWidth * 0.6 : 555;
      onResize(sidebarWidth);
    }, [isFullScreen, onResize]);

  
    const orderTotals = orders.reduce(
      (totals, order) => {
        totals.totalQty += order.totalQty;
        totals.delivered += order.delivered;
        totals.pending += order.pending;
        return totals;
      },
      { totalQty: 0, delivered: 0, pending: 0 }
    );

    // Campaign calculations
    let campaignsWithCalculations = [];
    let previousBalanceDelivered = orderTotals.delivered;
    let previousBalanceTotalQty = orderTotals.totalQty;
    campaigns.forEach((campaign) => {
      const estimatedQtyProduct = Math.ceil(
        campaign.qty * campaign.rateOfResponse * campaign.estimatedPerOrder
      );

      const balanceDelivered = previousBalanceDelivered - estimatedQtyProduct;
      const balanceTotalQty = previousBalanceTotalQty - estimatedQtyProduct;

      previousBalanceDelivered = balanceDelivered;
      previousBalanceTotalQty = balanceTotalQty;

      campaignsWithCalculations.push({
        ...campaign,
        estimatedQtyProduct,
        balanceDelivered,
        balanceTotalQty,
      });
    });
    const handleImageClick = (imageUrl) => {
      setModalImage(imageUrl);
      setShowModal(true);
    };

    return (
      <div
        ref={ref}
        className="product-details-tab"
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: "#fff",
          overflowY: "auto",
        }}
      >
        <div
          className="tab-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "15px",
            borderBottom: "1px solid #ddd",
            backgroundColor: "#f8f9fa",
          }}
        >
          <h5 style={{ margin: 0 }}>
            {productId
              ? variationId
                ? variation?.product_variation_desc
                : product?.name
              : "Create New Product"}
          </h5>
          <div>

          </div>
        </div>

          <div style={{ padding: "15px" }}>
            {product ? (
              <div>
                {/* Product Overview Section */}
                <div className="card mb-4">
                  <div className="card-body add-product pb-0">
                    <h6>Product Information</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <p><strong>Client:</strong> {product.client_name || "N/A"}</p>
                      </div>
                      <div className="col-md-6">
                        <p><strong>Brand:</strong> {product.brand_details?.brand_name || "N/A"}</p>
                      </div>
                      <div className="col-md-6">
                        <p><strong>Product Name:</strong> {product.name || "N/A"}</p>
                      </div>
                      <div className="col-md-6">
                        <p><strong>Product Code:</strong> {product.product_code || "N/A"}</p>
                      </div>
                      <div className="col-md-6">
                        <p><strong>Type:</strong> {product.product_type_name || "N/A"}</p>
                      </div>
                      <div className="col-md-6">
                        <p><strong>Universe:</strong> {product.product_universe_name || "N/A"}</p>
                      </div>
                      {warehouseId && (
                        <>
                          <div className="col-md-6">
                            <p><strong>Actual Stock Level:</strong> {actualStockLevel}</p>
                          </div>
                          <div className="col-md-6">
                            <p><strong>Inventory Date:</strong> {inventoryDate || "N/A"}</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {productId && variationId && warehouseId && (
                  <InventorySection
                    vId={variationId}
                    sku={variation?.product_variation_desc}
                    onlyHistory={true}
                    warehouseId={warehouseId}
                  />
                )}
                {/* Orders Section */}
                <div className="card mb-4">
                  <div className="card-body add-product pb-0">
                    <h6>Orders</h6>
                    {orders.length > 0 ? (
                      <>
                        <div className="row mb-3">
                          <div className="col-md-4 text-center">
                            <div className="border p-3">
                              <strong>Total Quantity</strong>
                              <div className="h5 mt-1">{orderTotals.totalQty}</div>
                            </div>
                          </div>
                          <div className="col-md-4 text-center">
                            <div className="border p-3">
                              <strong>Delivered</strong>
                              <div className="h5 mt-1">{orderTotals.delivered}</div>
                            </div>
                          </div>
                          <div className="col-md-4 text-center">
                            <div className="border p-3">
                              <strong>Pending</strong>
                              <div className="h5 mt-1">{orderTotals.pending}</div>
                            </div>
                          </div>
                        </div>
                        <div className="table-responsive">
                          <table className="table table-striped">
                            <thead>
                              <tr>
                                <th>Invoice Date</th>
                                <th>Invoice Number</th>
                                <th>Total Quantity</th>
                                <th>Delivered</th>
                                <th>Pending</th>
                              </tr>
                            </thead>
                            <tbody>
                              {orders.map((order, index) => (
                                <tr key={index}>
                                  <td>{order.date || "—"}</td>
                                  <td>{order.order || "—"}</td>
                                  <td>{order.totalQty}</td>
                                  <td>{order.delivered}</td>
                                  <td>{order.pending}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <p>No orders found for this product</p>
                    )}
                  </div>
                </div>
                {/* Campaigns Section */}
                <div className="card mb-4">
                  <div className="card-body add-product pb-0">
                    <h6>Campaigns</h6>
                    {campaignsWithCalculations.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-striped">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Campaign Code</th>
                              <th>Quantity</th>
                              <th>Response Rate</th>
                              <th>Per Order</th>
                              <th>Est. Product Qty</th>
                              <th>Balance (Delivered)</th>
                              <th>Balance (Total)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {campaignsWithCalculations.map((campaign, index) => (
                              <tr key={index}>
                                <td>{campaign.date || "—"}</td>
                                <td>{campaign.campaignCode || "—"}</td>
                                <td>{campaign.qty}</td>
                                <td>{(campaign.rateOfResponse * 100).toFixed(2)}%</td>
                                <td>{campaign.estimatedPerOrder}</td>
                                <td>{campaign.estimatedQtyProduct}</td>
                                <td className={campaign.balanceDelivered < 0 ? 'text-danger' : ''}>
                                  {campaign.balanceDelivered}
                                </td>
                                <td className={campaign.balanceTotalQty < 0 ? 'text-danger' : ''}>
                                  {campaign.balanceTotalQty}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p>No campaigns found for this product</p>
                    )}
                  </div>
                </div>
                {/* Variation Details Section */}
                {variation && (
                  <div className="card mb-4">
                    <div className="card-body add-product pb-0">
                      <h6>Other Details</h6>
                      <div className="row">
                        <div className="col-md-6">
                          <p><strong>Formula from Lab:</strong> {variation.formula_variation_from_lab === "Approved" ? "✅ Approved" : variation.formula_variation_from_lab === "Requested" ? "📝 Requested" : "—"}</p>
                          <p><strong>Supplement Facts:</strong> {variation.supplement_facts || "—"}</p>
                          <p><strong>UPC Code:</strong> {variation.upc_code || "—"}</p>
                          <p><strong>Manufactured Description:</strong> {variation.manufactured_description || "—"}</p>
                        </div>
                        <div className="col-md-6">
                          <p><strong>Front Claims:</strong> {variation.front_claims || "—"}</p>
                          <p><strong>FDA Statements:</strong> {variation.fda_statements || "—"}</p>
                          <p><strong>Labels Approved by Lab:</strong> {variation.labels_approved_by_lab === "Approved" ? "✅ Approved" : variation.labels_approved_by_lab === "Pending" ? "🕒 Pending" : "—"}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Product Variations Section */}
                <div className="card mb-4">
                  <div className="card-body add-product pb-0">
                    <h6>All Product Variations</h6>
                    {productVariations.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover" style={{ backgroundColor: '#fff' }}>
                          <thead style={{ backgroundColor: '#f8f9fa' }}>
                            <tr>
                              <th style={{ 
                                fontWeight: '600',
                                fontSize: '14px',
                                color: '#212529',
                                padding: '12px 16px',
                                borderBottom: '2px solid #dee2e6'
                              }}>Status</th>
                              <th style={{ 
                                fontWeight: '600',
                                fontSize: '14px',
                                color: '#212529',
                                padding: '12px 16px',
                                borderBottom: '2px solid #dee2e6'
                              }}>Product Variation Type</th>
                              <th style={{ 
                                fontWeight: '600',
                                fontSize: '14px',
                                color: '#212529',
                                padding: '12px 16px',
                                borderBottom: '2px solid #dee2e6'
                              }}>Product Variation</th>
                              <th style={{ 
                                fontWeight: '600',
                                fontSize: '14px',
                                color: '#212529',
                                padding: '12px 16px',
                                borderBottom: '2px solid #dee2e6'
                              }}>Program Time</th>
                              <th style={{ 
                                fontWeight: '600',
                                fontSize: '14px',
                                color: '#212529',
                                padding: '12px 16px',
                                borderBottom: '2px solid #dee2e6'
                              }}>Posology</th>
                              <th style={{ 
                                fontWeight: '600',
                                fontSize: '14px',
                                color: '#212529',
                                padding: '12px 16px',
                                borderBottom: '2px solid #dee2e6'
                              }}>Logistic Codification</th>
                              <th style={{ 
                                fontWeight: '600',
                                fontSize: '14px',
                                color: '#212529',
                                padding: '12px 16px',
                                borderBottom: '2px solid #dee2e6'
                              }}>Product Variation Code</th>
                              <th style={{ 
                                fontWeight: '600',
                                fontSize: '14px',
                                color: '#212529',
                                padding: '12px 16px',
                                borderBottom: '2px solid #dee2e6'
                              }}>Description</th>
                              <th style={{ 
                                fontWeight: '600',
                                fontSize: '14px',
                                color: '#212529',
                                padding: '12px 16px',
                                borderBottom: '2px solid #dee2e6'
                              }}>Photos</th>
                            </tr>
                          </thead>
                          <tbody>
                            {productVariations.map((variation, index) => (
                              <tr key={index}>
                                <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>{variation.status_name || '—'}</td>
                                <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>{variation.product_variation_type_name || '—'}</td>
                                <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>{variation.product_variation || '—'}</td>
                                <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>{variation.program_time || '—'}</td>
                                <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>{variation.posology || '—'}</td>
                                <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>{variation.logistic_codification || '—'}</td>
                                <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>{variation.product_variation_code || '—'}</td>
                                <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>{variation.product_variation_desc || '—'}</td>
                                <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                                  {variation.images && variation.images.length > 0 ? (
                                    <div className="d-flex flex-wrap gap-1">
                                      {variation.images.slice(0, 3).map((img, imgIndex) => (
                                        <img
                                          key={imgIndex}
                                          src={img.signed_image_url}
                                          alt={`variation-${imgIndex}`}
                                          style={{
                                            width: 30,
                                            height: 30,
                                            marginRight: 5,
                                            cursor: 'pointer',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px'
                                          }}
                                          onClick={() => handleImageClick(img.signed_image_url)}
                                        />
                                      ))}
                                      {variation.images.length > 3 && (
                                        <div className="d-flex align-items-center justify-content-center" 
                                          style={{
                                            width: "30px", 
                                            height: "30px", 
                                            border: "1px solid #ddd", 
                                            borderRadius: "4px", 
                                            backgroundColor: "#f8f9fa"
                                          }}
                                        >
                                          <small>+{variation.images.length - 3}</small>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span>No images</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p>No variations available for this product</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p>Loading...</p>
            )}
          </div>
   
        <div style={{ height: 100 }}></div>

        {/* Image modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Body style={{ textAlign: "center" }}>
            <img
              src={modalImage}
              alt="Preview"
              style={{ maxWidth: "100%", maxHeight: "80vh" }}
            />
          </Modal.Body>
        </Modal>
      </div>
    );
  }
);

ProductDetailsView.displayName = "ProductDetailsView";
export default ProductDetailsView;

